import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiPost } from '../services/api'
import {
  Clock, AlertTriangle, Shield, Eye, EyeOff, ChevronLeft,
  ChevronRight, Flag, CheckCircle, Lock, Monitor, Camera,
  MousePointer, Keyboard
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Question {
  id: string
  type: 'multiple_choice' | 'true_false' | 'essay'
  question: string
  options?: string[]
  correctAnswer?: string
  points: number
}

export default function ExamTaking() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [flagged, setFlagged] = useState<Set<string>>(new Set())
  const [timeLeft, setTimeLeft] = useState(7200) // 120 minutes
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [warningCount, setWarningCount] = useState(0)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [showCameraFeed, setShowCameraFeed] = useState(false)
  const [tabSwitchCount, setTabSwitchCount] = useState(0)
  const examContainerRef = useRef<HTMLDivElement>(null)

  const questions: Question[] = [
    {
      id: 'q1', type: 'multiple_choice',
      question: 'What is the derivative of f(x) = x²?',
      options: ['x', '2x', 'x²', '2'],
      correctAnswer: '2x',
      points: 5
    },
    {
      id: 'q2', type: 'multiple_choice',
      question: 'Solve for x: 2x + 5 = 13',
      options: ['3', '4', '5', '6'],
      correctAnswer: '4',
      points: 5
    },
    {
      id: 'q3', type: 'true_false',
      question: 'The integral of e^x is e^x + C',
      options: ['True', 'False'],
      correctAnswer: 'True',
      points: 3
    },
    {
      id: 'q4', type: 'multiple_choice',
      question: 'What is the value of π (pi) to 2 decimal places?',
      options: ['3.12', '3.14', '3.16', '3.18'],
      correctAnswer: '3.14',
      points: 5
    },
    {
      id: 'q5', type: 'essay',
      question: 'Explain the fundamental theorem of calculus in your own words.',
      points: 10
    },
  ]

  // ANTI-CHEATING SYSTEM
  useEffect(() => {
    // Enter fullscreen
    const enterFullScreen = async () => {
      try {
        await document.documentElement.requestFullscreen()
        setIsFullScreen(true)
      } catch {
        toast.error('Fullscreen required for exam security')
      }
    }
    enterFullScreen()

    // Prevent right click
    const preventRightClick = (e: MouseEvent) => {
      e.preventDefault()
      triggerWarning('Right-clicking is not allowed during exams')
    }
    document.addEventListener('contextmenu', preventRightClick)

    // Prevent copy/paste/cut
    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault()
      triggerWarning('Copy/Paste is disabled during exams')
    }
    document.addEventListener('copy', preventCopy)
    document.addEventListener('paste', preventCopy)
    document.addEventListener('cut', preventCopy)

    // Prevent keyboard shortcuts
    const preventShortcuts = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (['c', 'v', 'x', 'a', 'p', 's', 'u', 'j'].includes(e.key.toLowerCase())) {
          e.preventDefault()
          triggerWarning(`Keyboard shortcut Ctrl+${e.key.toUpperCase()} is blocked`)
        }
      }
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault()
        triggerWarning('Developer tools are blocked during exams')
      }
      if (e.altKey && e.key === 'Tab') {
        e.preventDefault()
        triggerWarning('Alt+Tab is blocked')
      }
    }
    document.addEventListener('keydown', preventShortcuts)

    // Detect tab switch / window blur
    const handleBlur = () => {
      setTabSwitchCount(prev => {
        const newCount = prev + 1
        if (newCount >= 3) {
          autoSubmit('Tab switching limit exceeded')
        } else {
          triggerWarning(`Tab switch detected! Warning ${newCount}/3. Exam will auto-submit after 3 violations.`)
        }
        return newCount
      })
    }
    window.addEventListener('blur', handleBlur)

    // Detect print screen
    const handlePrintScreen = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        e.preventDefault()
        triggerWarning('Screenshots are not allowed during exams')
      }
    }
    document.addEventListener('keyup', handlePrintScreen)

    // Fullscreen exit detection
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false)
        triggerWarning('Exiting fullscreen is not allowed! Re-entering...')
        document.documentElement.requestFullscreen().catch(() => {
          autoSubmit('Fullscreen exit detected')
        })
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('contextmenu', preventRightClick)
      document.removeEventListener('copy', preventCopy)
      document.removeEventListener('paste', preventCopy)
      document.removeEventListener('cut', preventCopy)
      document.removeEventListener('keydown', preventShortcuts)
      window.removeEventListener('blur', handleBlur)
      document.removeEventListener('keyup', handlePrintScreen)
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      if (document.fullscreenElement) {
        document.exitFullscreen()
      }
    }
  }, [])

  // Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          autoSubmit('Time expired')
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const triggerWarning = (message: string) => {
    setWarningCount(prev => prev + 1)
    setShowWarning(true)
    toast.error(message, { duration: 4000 })
    setTimeout(() => setShowWarning(false), 4000)
  }

  const autoSubmit = (reason: string) => {
    toast.error(`Exam auto-submitted: ${reason}`, { duration: 5000 })
    handleSubmit()
  }

  const handleSubmit = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await apiPost(`/exams/${id}/submit`, { answers, warnings: warningCount, tabSwitches: tabSwitchCount })
      toast.success('Exam submitted successfully!')
      navigate('/exams')
    } catch {
      toast.error('Submission failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  const currentQ = questions[currentQuestion]
  const progress = ((currentQuestion + 1) / questions.length) * 100

  return (
    <div ref={examContainerRef} className="fixed inset-0 bg-dark z-50 flex flex-col">
      {/* Security Header */}
      <div className="h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1 bg-danger/20 text-danger rounded-lg text-sm font-medium">
            <Shield className="w-4 h-4" />
            ANTI-CHEAT ACTIVE
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Monitor className="w-3 h-3" /> Screen monitored
            <Camera className="w-3 h-3 ml-2" /> Camera active
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium ${
            timeLeft < 300 ? 'bg-danger/20 text-danger animate-pulse' : 'bg-slate-800'
          }`}>
            <Clock className="w-4 h-4" />
            {formatTime(timeLeft)}
          </div>
          <div className="text-xs text-slate-500">
            Q {currentQuestion + 1} / {questions.length}
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      {showWarning && (
        <div className="bg-danger/20 border-b border-danger/30 px-4 py-2 flex items-center gap-2 animate-pulse">
          <AlertTriangle className="w-4 h-4 text-danger" />
          <span className="text-sm text-danger font-medium">
            SECURITY WARNING #{warningCount}: Suspicious activity detected!
          </span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Question Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Question */}
            <div className="glass rounded-xl p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">
                  {currentQ.type.replace('_', ' ').toUpperCase()} • {currentQ.points} pts
                </span>
                <button
                  onClick={() => {
                    const newFlagged = new Set(flagged)
                    if (newFlagged.has(currentQ.id)) newFlagged.delete(currentQ.id)
                    else newFlagged.add(currentQ.id)
                    setFlagged(newFlagged)
                  }}
                  className={`p-1 rounded ${flagged.has(currentQ.id) ? 'text-warning' : 'text-slate-500 hover:text-warning'}`}
                >
                  <Flag className="w-4 h-4" />
                </button>
              </div>
              <h2 className="text-lg font-medium mb-6">{currentQ.question}</h2>

              {currentQ.type === 'essay' ? (
                <textarea
                  value={answers[currentQ.id] || ''}
                  onChange={(e) => setAnswers({ ...answers, [currentQ.id]: e.target.value })}
                  placeholder="Type your answer here..."
                  className="w-full h-48 p-4 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary resize-none"
                />
              ) : (
                <div className="space-y-3">
                  {currentQ.options?.map((option, i) => (
                    <button
                      key={i}
                      onClick={() => setAnswers({ ...answers, [currentQ.id]: option })}
                      className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-all text-left ${
                        answers[currentQ.id] === option
                          ? 'border-primary bg-primary/10'
                          : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        answers[currentQ.id] === option ? 'border-primary' : 'border-slate-600'
                      }`}>
                        {answers[currentQ.id] === option && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                      </div>
                      <span>{option}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                disabled={currentQuestion === 0}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              {currentQuestion === questions.length - 1 ? (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to submit? You cannot change answers after submission.')) {
                      handleSubmit()
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-2 bg-success hover:bg-success/80 rounded-lg font-medium transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  {isSubmitting ? 'Submitting...' : 'Submit Exam'}
                </button>
              ) : (
                <button
                  onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))}
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Question Navigator Sidebar */}
        <div className="w-64 bg-slate-900 border-l border-slate-800 p-4 overflow-y-auto">
          <h3 className="font-semibold mb-4">Questions</h3>
          <div className="grid grid-cols-4 gap-2">
            {questions.map((q, i) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(i)}
                className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                  currentQuestion === i ? 'bg-primary text-white' :
                  answers[q.id] ? 'bg-success/20 text-success' :
                  flagged.has(q.id) ? 'bg-warning/20 text-warning' :
                  'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-success/20 rounded" /> Answered
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-warning/20 rounded" /> Flagged
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-slate-800 rounded" /> Unanswered
            </div>
          </div>

          <div className="mt-6 p-3 bg-slate-800/50 rounded-lg">
            <p className="text-xs text-slate-500 mb-2">Security Status</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-1 text-success">
                <Lock className="w-3 h-3" /> Fullscreen active
              </div>
              <div className="flex items-center gap-1 text-success">
                <Eye className="w-3 h-3" /> Screen monitored
              </div>
              <div className="flex items-center gap-1 text-success">
                <Keyboard className="w-3 h-3" /> Shortcuts blocked
              </div>
              <div className="flex items-center gap-1 text-success">
                <MousePointer className="w-3 h-3" /> Copy/paste disabled
              </div>
            </div>
            {warningCount > 0 && (
              <div className="mt-2 p-2 bg-danger/20 rounded text-danger text-xs">
                Warnings: {warningCount}/5
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
